// Crew cards show the same serf and hand-held gear as the village. Render a small
// batch once when the menu first opens, then use ordinary images on every refresh.
import {
  AmbientLight, DirectionalLight, DoubleSide, Mesh, MeshLambertMaterial,
  OrthographicCamera, Scene, Vector3, WebGLRenderer,
} from 'three'
import type { ItemType, UnitWire } from '../../shared/types'
import { unitGeometry } from '../render3d/models3d/units3d'

const LOOKS = ['idle', 'hammer', 'hoe', 'axe', 'pick', 'shovel', 'rod', 'spearman', 'swordsman', 'archer'] as const
type Look = typeof LOOKS[number]
const ITEM_LOOK: Record<ItemType, Look> = {
  hammer: 'hammer', smithhammer: 'hammer', hoe: 'hoe', axe: 'axe', pick: 'pick',
  shovel: 'shovel', rod: 'rod', spear: 'spearman', sword: 'swordsman', bow: 'archer',
}
const WIDTH = 160, HEIGHT = 176
// A crew belongs to one faction. Keep a small fixed budget even if the player
// changes faction repeatedly; further colors can use the lightweight vector.
const MAX_COLORS = 4
const portraits = new Map<string, Map<Look, string>>()
let webglUnavailable = false

function normalizedColor(color: string): string {
  if (/^#[\da-f]{6}$/i.test(color)) return color.toLowerCase()
  if (/^#[\da-f]{3}$/i.test(color)) return `#${[...color.slice(1)].map(c => c + c).join('').toLowerCase()}`
  return '#69a79a'
}

function renderPortraits(color: string): Map<Look, string> | undefined {
  const cached = portraits.get(color)
  if (cached || webglUnavailable || portraits.size >= MAX_COLORS) return cached
  const images = new Map<Look, string>()
  portraits.set(color, images)
  let renderer: WebGLRenderer | undefined
  const material = new MeshLambertMaterial({ vertexColors: true, flatShading: true, side: DoubleSide })
  try {
    renderer = new WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' })
    renderer.setSize(WIDTH, HEIGHT)
    renderer.setClearColor(0, 0)
    const scene = new Scene()
    scene.add(new AmbientLight(0xe5edf1, 1.6))
    const sun = new DirectionalLight(0xffe6b8, 2.5)
    sun.position.set(-30, 60, -50)
    scene.add(sun)
    const camera = new OrthographicCamera(-1, 1, 1, -1, .1, 300)
    // Serfs face -Z. A slightly elevated three-quarter view keeps their faces,
    // tunics and actual held tools visible, including the long spear and rod.
    camera.position.set(38, 31, -60)
    camera.lookAt(0, 11, -3)
    camera.updateMatrixWorld(true)
    const point = new Vector3()
    const aspect = WIDTH / HEIGHT
    for (const look of LOOKS) {
      const geometry = unitGeometry('serf', look, color)
      const mesh = new Mesh(geometry, material)
      scene.add(mesh)
      // Fit the actual projected vertices instead of a wide world-space box.
      // That preserves readable faces without clipping a tool at the image edge.
      const positions = geometry.getAttribute('position')
      let left = Infinity, right = -Infinity, bottom = Infinity, top = -Infinity
      for (let i = 0; i < positions.count; i++) {
        point.fromBufferAttribute(positions, i).applyMatrix4(camera.matrixWorldInverse)
        left = Math.min(left, point.x); right = Math.max(right, point.x)
        bottom = Math.min(bottom, point.y); top = Math.max(top, point.y)
      }
      const cx = (left + right) / 2, cy = (bottom + top) / 2
      const halfHeight = Math.max((top - bottom) / 2, (right - left) / (2 * aspect)) * 1.1
      camera.left = cx - halfHeight * aspect; camera.right = cx + halfHeight * aspect
      camera.top = cy + halfHeight; camera.bottom = cy - halfHeight
      camera.updateProjectionMatrix()
      renderer.render(scene, camera)
      images.set(look, renderer.domElement.toDataURL('image/png'))
      scene.remove(mesh)
    }
  } catch {
    // Do not repeatedly ask a device with no available WebGL context to create one.
    webglUnavailable = true
  } finally {
    // Geometry belongs to the shared game model cache; only release this render.
    material.dispose()
    renderer?.dispose()
    renderer?.forceContextLoss()
  }
  return images
}

function fallback(color: string): HTMLElement {
  const wrapper = document.createElement('span')
  wrapper.className = 'crew-portrait'
  wrapper.setAttribute('aria-hidden', 'true')
  wrapper.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 88" fill="none" focusable="false">
    <path d="m24 56 14 1-2 23H23Zm16 1 13-1 5 23H44Z" fill="#665747"/>
    <path d="m23 74 13 1v7H20Zm21 1 13-1 5 7H45Z" fill="#41382f"/>
    <path d="m24 30 15-5 14 6 5 23-18 9-18-7Z" data-tunic=""/>
    <path d="m40 28 13 3 5 23-18 9Z" fill="#142720" opacity=".22"/>
    <path d="m22 52 18 5 17-7 1 5-18 8-18-6Z" fill="#584735"/>
    <path d="m38 55 6-1v7l-6 1Z" fill="#d8b46a"/>
    <path d="m19 32 8-1-4 17-7 1Zm34 1 7 3 4 15-7 1Z" data-tunic=""/>
    <path d="m16 48 7-1-1 11-7 1Zm41 3 7-1 2 9-7 2Z" fill="#cf9d73"/>
    <path d="m29 13 14-6 12 9-2 12-13 8-12-10Z" fill="#d4a378"/>
    <path d="m44 9 11 7-2 12-13 8 2-16Z" fill="#b58460"/>
    <path d="m27 13 5-9 14-2 12 11-3 6-12-5-13 5Z" fill="#624a32"/>
    <path d="m31 20 3-1v3l-3 1Zm12-1 3-1v3l-3 1Z" fill="#41382f"/>
    <path d="m37 21 4-1 1 5-5 1Z" fill="#e1b48c"/>
  </svg>`
  for (const tunic of wrapper.querySelectorAll('[data-tunic]')) tunic.setAttribute('fill', color)
  return wrapper
}

/** Decorative model portrait; the crew card supplies the person's name and gear. */
export function crewPortrait(u: UnitWire, color: string): HTMLElement {
  const tint = normalizedColor(color)
  const look = u.person?.held ? ITEM_LOOK[u.person.held] : 'idle'
  const source = renderPortraits(tint)?.get(look)
  if (!source) return fallback(tint)
  const image = document.createElement('img')
  image.className = 'crew-portrait'
  image.setAttribute('aria-hidden', 'true')
  image.src = source; image.alt = ''; image.width = WIDTH; image.height = HEIGHT
  image.style.objectFit = 'contain'
  image.draggable = false
  return image
}
